import { Link, useLocation } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { ChevronLeft, ChevronRight, Home, BookOpen, Layers, Menu, X, Check, Circle, CheckCircle } from 'lucide-react';
import { Header } from './Header';
import { useState, useEffect } from 'react';
import { useTutorialProgress } from '../hooks';

export interface TutorialPage {
  id: string;
  title: string;
  path: string;
}

export interface TutorialLevel {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  pages: TutorialPage[];
}

interface TutorialLayoutProps {
  children: React.ReactNode;
  levels: TutorialLevel[];
}

export const TutorialLayout: React.FC<TutorialLayoutProps> = ({
  children,
  levels
}) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isCompleted, toggleComplete, getCompletedCount, setLastVisited } = useTutorialProgress();

  // Find current position in tutorial
  const allPages = levels.flatMap(level =>
    level.pages.map(page => ({ ...page, levelId: level.id, levelTitle: level.title }))
  );
  const currentIndex = allPages.findIndex(p => p.path === location.pathname);
  const currentPage = currentIndex >= 0 ? allPages[currentIndex] : null;
  const prevPage = currentIndex > 0 ? allPages[currentIndex - 1] : null;
  const nextPage = currentIndex < allPages.length - 1 ? allPages[currentIndex + 1] : null;

  // Calculate progress based on completed pages
  const totalPages = allPages.length;
  const completedCount = getCompletedCount();
  const progressPercent = Math.round((completedCount / totalPages) * 100);

  // Current page completion status
  const isCurrentPageCompleted = currentPage ? isCompleted(currentPage.id) : false;

  // Track last visited page
  useEffect(() => {
    if (location.pathname) {
      setLastVisited(location.pathname);
    }
  }, [location.pathname, setLastVisited]);

  // Sidebar content component (reused for mobile and desktop)
  const SidebarContent = () => (
    <>
      <Link
        to="/"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted mb-1"
      >
        <Home className="w-4 h-4" />
        Home
      </Link>
      <Link
        to="/tutorials"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted mb-4"
      >
        <BookOpen className="w-4 h-4" />
        All Tutorials
      </Link>

      {/* Progress indicator */}
      <div className="px-3 py-2 mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>Progress</span>
          <span>{completedCount}/{totalPages}</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {levels.map((level) => {
        const levelPages = level.pages;
        const levelCompletedCount = levelPages.filter(p => isCompleted(p.id)).length;

        return (
          <div key={level.id} className="mb-4">
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>{level.title}</span>
              <span className="text-[10px] font-normal">{levelCompletedCount}/{levelPages.length}</span>
            </div>
            <ul className="space-y-0.5">
              {level.pages.map((page) => {
                const completed = isCompleted(page.id);
                const isActive = location.pathname === page.path;

                return (
                  <li key={page.id} className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleComplete(page.id);
                      }}
                      className={`w-6 h-6 flex items-center justify-center rounded hover:bg-muted transition-colors ${
                        completed ? 'text-green-500' : 'text-muted-foreground/30 hover:text-muted-foreground/60'
                      }`}
                      title={completed ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                      {completed ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Circle className="w-4 h-4" />
                      )}
                    </button>
                    <Link
                      to={page.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex-1 py-1.5 px-2 text-sm rounded-md transition-colors truncate ${
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {page.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <div className="mt-4 pt-4 border-t border-border">
        <Link
          to="/sandbox"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
        >
          <Layers className="w-4 h-4" />
          Component Sandbox
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header showProgress progressValue={progressPercent} />

      <div className="flex">
        {/* Mobile bottom sheet toggle - visible below lg */}
        <Button
          variant="default"
          size="sm"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 shadow-lg rounded-full px-6"
          id="mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Menu className="w-4 h-4 mr-2" />
          Tutorials
        </Button>

        {/* Mobile bottom sheet overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            id="mobile-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile bottom sheet */}
        <aside
          className={`fixed left-0 right-0 bottom-0 bg-background border-t border-border rounded-t-2xl z-50 transform transition-transform duration-300 ease-out ${
            sidebarOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ maxHeight: '85vh' }}
          id="mobile-sheet"
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-border">
            <h3 className="font-semibold">Tutorials</h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <nav className="overflow-y-auto p-4" style={{ maxHeight: 'calc(85vh - 80px)' }}>
            <SidebarContent />
          </nav>
        </aside>

        {/* Sidebar - Desktop - visible on lg and up */}
        <aside className="w-64 shrink-0 border-r border-border min-h-[calc(100vh-3.5rem)] bg-muted/30" id="desktop-sidebar">
          <nav className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto p-4">
            <SidebarContent />
          </nav>
        </aside>

        {/* Responsive styles */}
        <style>{`
          @media (max-width: 1023px) {
            #desktop-sidebar { display: none !important; }
          }
          @media (min-width: 1024px) {
            #desktop-sidebar { display: block !important; }
            #mobile-menu-btn { display: none !important; }
            #mobile-overlay { display: none !important; }
            #mobile-sheet { display: none !important; }
          }
        `}</style>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="px-6 py-8 pb-24 lg:pb-8">
            {children}

            {/* Mark Complete & Navigation */}
            <div className="mt-8 pt-6 border-t border-border space-y-4">
              {/* Mark Complete Button */}
              {currentPage && (
                <Button
                  variant={isCurrentPageCompleted ? "outline" : "default"}
                  onClick={() => toggleComplete(currentPage.id)}
                  className="w-full"
                >
                  {isCurrentPageCompleted ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Completed - Click to Unmark
                    </>
                  ) : (
                    <>
                      <Circle className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </>
                  )}
                </Button>
              )}

              {/* Prev/Next Navigation */}
              <div className="flex items-center justify-between gap-4">
                {prevPage ? (
                  <Link to={prevPage.path} className="flex-1 max-w-[45%]">
                    <Button variant="outline" className="w-full justify-start h-auto py-3">
                      <ChevronLeft className="w-4 h-4 mr-2 shrink-0" />
                      <div className="text-left truncate">
                        <div className="text-xs text-muted-foreground">Previous</div>
                        <div className="truncate text-sm">{prevPage.title}</div>
                      </div>
                    </Button>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}

                {nextPage ? (
                  <Link to={nextPage.path} className="flex-1 max-w-[45%]">
                    <Button variant="outline" className="w-full justify-end h-auto py-3">
                      <div className="text-right truncate">
                        <div className="text-xs text-muted-foreground">Next</div>
                        <div className="truncate text-sm">{nextPage.title}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
